import React, { useEffect, useMemo, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import SettlePaymentName from './SettlePaymentName';
import SettlePaymentSummary from './SettlePaymentSummary';
import SettlePaymentChargeCurrency from './SettlePaymentChargeCurrency';
import PaymentStore from '../../../../stores/PaymentStore';
import PaymentConstants from '../../../../constants/PaymentConstants';
import helpers from './helpers';
import { Trans, useTranslation } from 'react-i18next';
import SettlePaymentHotelSummary from './SettlePaymentHotelSummary';
import SettlePaymentReservationsList from './SettlePaymentReservationsList.react';
import PaymentLinkConstants from '../../../../constants/PaymentLinkConstants';
import PaymentLinkStore from '../../../../stores/PaymentLinkStore';
import SettlePaymentCopyLinkFilled from './SettlePaymentCopyLinkFilled';
import PaymentLinkActions from '../../../../actions/PaymentLinkActions';
import { isAccountBooking } from '@/utils/AccountBankTransfer';

const SettlePaymentPayNow = ({
	profile,
	booking,
	bookings,
	isMultiMode,
	nextStep,
	onEditCommission,
	chargeCurrency,
	updateChargeCurrency,
	nameState,
	dispatchName,
}) => {
	const { t } = useTranslation();

	const [loading, setLoading] = useState(false);
	const [paymentData, setPaymentData] = useState(PaymentStore.getPaymentData());
	const [paymentLinkStatus, setPaymentLinkStatus] = useState(PaymentLinkStore.getStatus(booking.id));

	const [nameError, setNameError] = useState(false);
	const [nameErrorMessage, setNameErrorMessage] = useState('');

	useEffect(() => {
		function handleStoreChange() {
			setPaymentData(PaymentStore.getPaymentData());
			setPaymentLinkStatus(PaymentLinkStore.getStatus(booking.id));
		}

		PaymentStore.addChangeListener(handleStoreChange);
		PaymentLinkStore.addChangeListener(handleStoreChange);

		return () => {
			PaymentStore.removeChangeListener(handleStoreChange);
			PaymentLinkStore.removeChangeListener(handleStoreChange);
		};
	}, []);

	useEffect(() => {
		if (paymentData.status === PaymentConstants.STATUS.INITIALIZING) {
			setLoading(false);
		}
	}, [paymentData]);

	const payment_link = useMemo(() => {
		return helpers.getPaymentLink(booking, profile);
	}, [booking, profile]);

	const show_link = paymentLinkStatus === PaymentLinkConstants.STATUS.SUCCESS;
	// An Account request takes its invoice entity from the booking, so the name is never asked for.
	const showName = !isAccountBooking(booking);

	const validateForm = () => {
		if (!chargeCurrency) {
			return false;
		}

		if (showName && !nameState?.name && !nameState?.invoiceId) {
			setNameError(true);
			setNameErrorMessage(t('Bookings.SettlePayment.PleaseSelectAName'));
			return false;
		}

		setNameError(false);
		setNameErrorMessage('');

		return true;
	};

	return (
		<div className="settle-payment-wrap">
			<div className="settle-payment">
				<div>
					{show_link ? (
						<SettlePaymentCopyLinkFilled link={payment_link} />
					) : (
						showName && (
							<SettlePaymentName
								profile={profile}
								booking={booking}
								isMultiMode={isMultiMode}
								nameState={nameState}
								dispatchName={dispatchName}
								error={nameError}
								errorMessage={nameErrorMessage}
								clearError={() => setNameError(false)}
							/>
						)
					)}

					{(show_link || showName) && <hr />}

					{!isAccountBooking(booking) && (
						<SettlePaymentChargeCurrency
							chargeCurrency={chargeCurrency}
							updateChargeCurrency={updateChargeCurrency}
							disabled={show_link}
						/>
					)}

					<SettlePaymentSummary
						booking={booking}
						bookings={bookings}
						isMultiMode={isMultiMode}
						showEditCommission={!show_link && !isAccountBooking(booking)}
						onEditCommission={onEditCommission}
						chargeCurrency={chargeCurrency}
						profile={profile}
					/>
				</div>
				<div className="actions">
					{show_link ? (
						<>
							<div className="copy-link-text">
								<Trans i18nKey="EditPriceWarning"></Trans>
							</div>
							<Button
								variant="outlined"
								fullWidth
								onClick={() => {
									PaymentLinkActions.resetPaymentLink(booking.id);
								}}
							>
								{t('EditPrice')}
							</Button>
						</>
					) : (
						<Button
							variant="contained"
							fullWidth
							onClick={() => {
								if (!validateForm()) {
									return false;
								}

								nextStep();
							}}
							disabled={loading}
						>
							{loading ? <CircularProgress size={21} /> : t('Bookings.SettlePayment.Next')}
						</Button>
					)}
				</div>
			</div>
			{isMultiMode ? (
				<SettlePaymentReservationsList bookings={bookings} />
			) : (
				<SettlePaymentHotelSummary booking={booking} />
			)}
		</div>
	);
};

export default SettlePaymentPayNow;
